/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/NotificationsTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./NotificationsTable.module.css";

import {
  getCompanySizeOptions,
  getCompanyGeneralActivityOptions,
} from "../../data/selectOptions";
import { getSelectedOption } from "../../utils/common";
import { ReactComponent as DownloadFileIcon } from "../../assets/icons/NotificationsTable/DownloadFileIcon.svg";
import { useTranslation } from "react-i18next";

const NotificationsTable = ({
  notifications,
  // sortConfig,
  onSort,
  renderArrow,
  onDownload,
}) => {
  const { t } = useTranslation();

  const companySizeOptions = getCompanySizeOptions(t);
  const companyGeneralActivityOptions = getCompanyGeneralActivityOptions(t);

  const getFiltersUsedList = (notification) => {
    // fields: audienciaParaSocios, audienciaParaNoSocios, tamanoDeLaEmpresa, sector, subSector, actividadGeneralDeLaEmpresa, fechaAltaSac
    const filters = {
      audienciaParaSocios: notification.audienciaParaSocios
        ? t("components.notificationsTable.filterForMembers")
        : null,
      audienciaParaNoSocios: notification.audienciaParaNoSocios
        ? t("components.notificationsTable.filterForNonMembers")
        : null,
      ...(notification.tamanoDeLaEmpresa != null
        ? {
            tamanoDeLaEmpresa: getSelectedOption(
              companySizeOptions,
              notification.tamanoDeLaEmpresa,
            )?.label,
          }
        : {}),
      sector:
        notification.sector !== null
          ? t("components.notificationsTable.filterSector")
          : null,
      subSector:
        notification.subSector !== null
          ? t("components.notificationsTable.filterSubsector")
          : null,
      actividadGeneralDeLaEmpresa: getSelectedOption(
        companyGeneralActivityOptions,
        notification.actividadGeneralDeLaEmpresa,
      )?.label,
      fechaAltaSac: notification.fechaAltaSac,
    };
    return Object.values(filters).filter(Boolean);
  };

  const getNotificationType = (notification) => {
    switch (notification.tipo) {
      case 0:
        return t("pages.notifications.notificationTypeOption1");
      case 1:
        return t("pages.notifications.notificationTypeOption2");
      case 2:
        return t("pages.notifications.notificationTypeOption3");
      default:
        return "";
    }
  };

  return (
    <div className={styles.usersTableContainer}>
      {/* Desktop/Laptop Table View */}
      <div className={styles.desktopView}>
        <table className={styles.usersTable}>
          <thead>
            <tr>
              <th onClick={onSort} className={styles.notificationSubjectHeader}>
                <div className={styles.sortableHeader}>
                  {t("components.notificationsTable.notificationSubject")}{" "}
                  {renderArrow()}
                </div>
              </th>
              <th className={styles.notificationTypeHeader}>
                {t("components.notificationsTable.notificationType")}
              </th>
              <th className={styles.notificationFiltersHeader}>
                {t("components.notificationsTable.notificationFilters")}
              </th>
              <th className={styles.notificationActionsHeader}>
                {t("components.notificationsTable.notificationActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {notifications.map((notification, index) => (
              <tr key={index}>
                <td className={`${styles.notificationSubjectData}`}>
                  <span className={styles.notificationSubjectText}>
                    {notification.asunto}
                  </span>
                </td>
                <td className={`${styles.notificationTypeData}`}>
                  <span className={styles.notificationSubjectText}>
                    {getNotificationType(notification)}
                  </span>
                </td>
                <td className={`${styles.notificationFiltersData}`}>
                  {getFiltersUsedList(notification).map((filterLabel, i) => (
                    <span key={i} className={styles.bubbleContainer}>
                      {filterLabel}
                    </span>
                  ))}
                </td>
                <td className={styles.actionsCell}>
                  <button
                    onClick={() => onDownload(notification)}
                    className={styles.actionButton}
                  >
                    <DownloadFileIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NotificationsTable;
